"use client";

import { useEffect, useState } from "react";
import { Calendar, Columns, MapPin, Phone } from "lucide-react";

const CITY_ORDER = ["guayaquil", "cuenca"];

const LOCATIONS = {
	guayaquil: {
		label: "Guayaquil",
		tagline: "Te esperamos en nuestras modernas instalaciones, ubicadas en el corazón médico de la ciudad",
		address: "Torre Médica Solaris Piso 5 Consultorio 110111 - Guayaquil",
		phone: "+593 98 338 6589",
		hours: ["Lunes a Viernes 10:00 - 19:00.", "Sabados 10:00 - 14:00.", "Domingos y Feriados Cerrado"],
		mapSrc: "https://maps.google.com/maps?q=Torre+médica+solaris,+Guayaquil+090513&t=&z=15&ie=UTF8&iwloc=&output=embed",
		mapLink:
			"https://maps.app.goo.gl/8su5QpcMUHrKMwNg8",
	},
	cuenca: {
		// TODO: reemplaza estos datos por los reales de la sede de Cuenca
		label: "Cuenca",
		tagline: "Nuestra sede en el austro, con la misma calidez y calidad de siempre",
		address: "Av. Don Bosco, y y La Pinta, 010205 Cuenca",
		phone: "+593 98 102 3968",
		hours: ["Lunes a Viernes 09:00 - 18:00.", "Sabados 09:00 - 13:00.", "Domingos y Feriados Cerrado"],
		mapSrc: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7969.318649458599!2d-79.02940196048453!3d-2.914006104656417!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x91cd2360086760ff%3A0x50c023a8389d403f!2sGS%20DENTAL%20CUENCA!5e0!3m2!1ses-419!2sec!4v1788795738403!5m2!1ses-419!2sec",
		mapLink: "https://www.google.com.ec/maps/place/GS+DENTAL+CUENCA/@-2.9140061,-79.029402,2567a,75.4y,177.04h,95.36t/data=!3m7!1e1!3m5!1sbcME7ovDyCCx_YFhIYnhSg!2e0!6shttps:%2F%2Fstreetviewpixels-pa.googleapis.com%2Fv1%2Fthumbnail%3Fcb_client%3Dmaps_sv.tactile%26w%3D900%26h%3D600%26pitch%3D-5.35686520156186%26panoid%3DbcME7ovDyCCx_YFhIYnhSg%26yaw%3D177.04346998652903!7i13312!8i6656!4m16!1m8!3m7!1s0x91cd2360086760ff:0x50c023a8389d403f!2sGS+DENTAL+CUENCA!8m2!3d-2.9140895!4d-79.029437!10e5!16s%2Fg%2F11ywg0fyxv!3m6!1s0x91cd2360086760ff:0x50c023a8389d403f!8m2!3d-2.9140895!4d-79.029437!10e5!16s%2Fg%2F11ywg0fyxv?entry=ttu&g_ep=EgoyMDI2MDkwMi4wIKXMDSoASAFQAw%3D%3D",
	},
};

export function LocationGsdental() {
	const [city, setCity] = useState("guayaquil");
	const [display, setDisplay] = useState("guayaquil");
	const [fading, setFading] = useState(false);

	// Cross-fade suave del título/tagline mientras el panel se desliza
	useEffect(() => {
		if (city === display) return;
		setFading(true);
		const t = setTimeout(() => {
			setDisplay(city);
			setFading(false);
		}, 200);
		return () => clearTimeout(t);
	}, [city, display]);

	const index = CITY_ORDER.indexOf(city);
	const shown = LOCATIONS[display];

	return (
		<div className="container mx-auto px-4">
			{/* Header de la sección */}
			<div className="text-center mb-12">
				<div style={{ display: "flex", flexDirection: "column", alignItems:"center"}}>
					<div className="inline-flex items-center space-x-2 bg-red-100 text-red-800 px-6 py-3 rounded-full text-sm font-medium mb-6">
						<MapPin className="size-4" />
						<span>Nuestras Ubicaciones</span>
					</div>

					{/* Selector deslizante de sede */}
					<div className="relative inline-flex bg-slate-100 rounded-full p-1 mb-6">
						<span
							className="absolute top-1 bottom-1 w-1/2 rounded-full bg-white shadow-md transition-transform duration-500 ease-out"
							style={{ transform: `translateX(${index * 100}%)` }}
						/>
						{CITY_ORDER.map((key) => (
							<button
								key={key}
								type="button"
								onClick={() => setCity(key)}
								aria-pressed={city === key}
								className={`relative z-10 px-8 py-2.5 text-sm font-semibold rounded-full transition-colors duration-300 ${
									city === key ? "text-[#BE203A]" : "text-slate-500"
								}`}
							>
								{LOCATIONS[key].label}
							</button>
						))}
					</div>
				</div>

				<h2 className="text-4xl md:text-5xl font-bold text-[#606060] mb-6 leading-tight">
					Visítanos en{" "}
					<span className={`text-[#BE203A] transition-opacity duration-200 ${fading ? "opacity-0" : "opacity-100"}`}>
						{shown.label}
					</span>
				</h2>
				<p
					className={`text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed transition-opacity duration-200 ${
						fading ? "opacity-0" : "opacity-100"
					}`}
				>
					{shown.tagline}
				</p>
			</div>

			{/* Mapa y información: el panel completo se desliza junto con el selector */}
			<div className="overflow-hidden rounded-2xl" style={{padding:"20px"}}>
				<div
					className="flex w-[200%] transition-transform duration-500 ease-out"
					style={{ transform: `translateX(-${index * 50}%)` }}
				>
					{CITY_ORDER.map((key) => {
						const loc = LOCATIONS[key];
						return (
							<div key={key} className="w-1/2 grid lg:grid-cols-2 gap-12 items-stretch px-1">
								{/* Información de contacto */}
								<div className="space-y-8">
									<div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-100 h-full">
										<h3 className="text-2xl font-bold text-[#606060] mb-6">Información de Contacto</h3>
										<div className="space-y-6">
											<div className="flex items-start space-x-4">
												<div className="bg-red-100 p-3 rounded-full flex-shrink-0">
													<MapPin className="text-primary-600" />
												</div>
												<div>
													<h4 className="font-semibold text-[#606060] mb-1">Dirección</h4>
													<p className="text-slate-600">{loc.address}</p>
												</div>
											</div>

											<div className="flex items-start space-x-4">
												<div className="bg-red-100 p-3 rounded-full flex-shrink-0">
													<Phone className="text-primary-600" />
												</div>
												<div>
													<h4 className="font-semibold text-[#606060] mb-1">Teléfono</h4>
													<p className="text-slate-600">{loc.phone}</p>
												</div>
											</div>

											<div className="flex items-start space-x-4">
												<div className="bg-red-100 p-3 rounded-full flex-shrink-0">
													<Calendar className="text-primary-600" />
												</div>
												<div>
													<h4 className="font-semibold text-[#606060] mb-1">Horarios</h4>
													{loc.hours.map((line) => (
														<p key={line} className="text-slate-600">
															{line}
														</p>
													))}
												</div>
											</div>
										</div>
									</div>
								</div>

								{/* Mapa de Google */}
								<div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-slate-100 h-full flex flex-col">
									<iframe
										src={loc.mapSrc}
										width="100%"
										style={{ border: 0 }}
										allowFullScreen
										loading="lazy"
										referrerPolicy="no-referrer-when-downgrade"
										title={`Ubicación GS Dental - ${loc.label}`}
										className="w-full flex-1"
									/>
									<div className="p-6 border-t border-slate-100 flex-shrink-0">
										<a
											href={loc.mapLink}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center justify-center w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-200 shadow-lg hover:shadow-xl"
										>
											<MapPin className="mx-2" />
											<p>COMO LLEGAR</p>
										</a>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
